import type {Evaluator, Version} from "@cm/graph-v3"
import {graphNode, versionChain} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {TemplateNode} from "#template/template-node/template-node"
import type {OnCompileContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {ColorValue, NumberLike, ToneMapping} from "#template/nodes/types"
import {colorValue, toneMapping} from "#template/nodes/validation"
import {numberLike} from "#template/nodes/number/fundamental"
import {DataObjectReference} from "#template/nodes/image/data-object-reference"
import type {Render} from "#template/nodes/render/render"
import {defaultsForToneMapping} from "#template/nodes/render/tone-mapping"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {ThisStructID} from "#template/runtime-graph/types"
import {AppendDataObjectSize} from "#template/runtime-graph/nodes/append-data-object-size"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import {visitNone} from "#template/template-node/visit-helpers"

const TD = TypeDescriptors

export type PostProcessRenderParameters = {
    mode: "whiteBackground"
    ev: NumberLike
    whiteBalance: NumberLike
    toneMapping: ToneMapping
    lutUrl?: string
    transparent: boolean
    composite: boolean
    backgroundColor: ColorValue
    backgroundImage?: DataObjectReference
    mask?: DataObjectReference
    processShadows: boolean
    shadowInner: number
    shadowOuter: number
    shadowFalloff: number
    shadowOpacity: number
    autoCrop: boolean
    autoCropMargin: number
}

export type PostProcessRenderConstructorParameters = Omit<PostProcessRenderParameters, "shadowOpacity"> & {shadowOpacity?: number}

type V0 = {
    render: Render | null
    mode: "whiteBackground"
    exposure: NumberLike
    whiteBalance: NumberLike
    toneMapping?: ToneMapping
    lutUrl?: string
    transparent: boolean
    composite: boolean
    backgroundColor: ColorValue
    processShadows: boolean
    shadowInner: number
    shadowOuter: number
    shadowFalloff: number
    autoCrop: boolean
    autoCropMargin: number
}
type V1 = V0 & {toneMapping: ToneMapping}
type V2 = Omit<V1, "exposure"> & {ev: NumberLike}
type V3 = Omit<V2, "render"> & {render: Render | undefined}
type V4 = Omit<V3, "render"> & {shadowOpacity: number}

const v0: Version<V0, V1> = {
    toNextVersion: (parameters) => {
        const {toneMapping, ...rest} = parameters
        return {...rest, toneMapping: toneMapping ?? defaultsForToneMapping("filmic")}
    },
}

const v1: Version<V1, V2> = {
    toNextVersion: (parameters) => {
        const {exposure, ...rest} = parameters
        return {...rest, ev: exposure}
    },
}

const v2: Version<V2, V3> = {
    toNextVersion: (parameters) => {
        const {render, ...rest} = parameters
        return {...rest, render: render ?? undefined}
    },
}

const v3: Version<V3, V4> = {
    toNextVersion: (parameters) => {
        const {render, ...rest} = parameters
        return {...rest, shadowOpacity: 1}
    },
}

const postProcessRenderDefinition: TemplateNodeDefinition<PostProcessRenderParameters, PostProcessRenderConstructorParameters> = {
    nodeClass: "PostProcessRender",
    validation: {
        parameters: z.object({
            mode: z.literal("whiteBackground"),
            ev: numberLike,
            whiteBalance: numberLike,
            toneMapping: toneMapping,
            lutUrl: z.optional(z.string()),
            transparent: z.boolean(),
            composite: z.boolean(),
            backgroundColor: colorValue,
            backgroundImage: z.optional(graphNode(DataObjectReference)),
            mask: z.optional(graphNode(DataObjectReference)),
            processShadows: z.boolean(),
            shadowInner: z.number(),
            shadowOuter: z.number(),
            shadowFalloff: z.number(),
            shadowOpacity: z._default(z.number(), 1),
            autoCrop: z.boolean(),
            autoCropMargin: z.number(),
        }),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([v0, v1, v2, v3]),
}

@registerTemplateGraphNode
export class PostProcessRender extends TemplateNode<PostProcessRenderParameters, PostProcessRenderConstructorParameters> {
    public override get definition() {
        return postProcessRenderDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<PostProcessRenderParameters> {
        const {evaluator, currentTemplate, templateDepth, path} = context
        const {displayList, preDisplayList} = currentTemplate
        const {templateScope, templateContext} = evaluator
        const {sceneManager, computeDataObjectReferences, lodType} = templateContext
        const {
            mode,
            ev,
            whiteBalance,
            toneMapping,
            lutUrl,
            transparent,
            composite,
            backgroundColor,
            backgroundImage,
            mask,
            processShadows,
            shadowInner,
            shadowOuter,
            shadowFalloff,
            shadowOpacity,
            autoCrop,
            autoCropMargin,
        } = this.parameters

        const scope = evaluator.getScope(this)

        const newPath = scope.pureLambda(scope.tuple(path, this), ([path, instance]) => [...path, instance], "newPath", {
            output: TD.ObjectPath,
        })

        const addDataObjectReference = (dataObject: DataObjectReference | undefined) => {
            if (!dataObject) return undefined

            const result = dataObject.getDataObject(scope, evaluator)

            if (computeDataObjectReferences && lodType === "pathTraced") {
                const referencedDataObject = scope.struct<SceneNodes.DataObjectReference>("DataObjectReference", {
                    type: "DataObjectReference",
                    id: `${scope.getPrefix()}:DataObjectReference@${dataObject.parameters.dataObjectId}`,
                    path: newPath,
                    dataObject: scope.node(AppendDataObjectSize, {sceneManager, input: result}).output,
                    origin: {
                        type: "postProcessRender",
                    },
                })

                preDisplayList.push(referencedDataObject)
            }

            return result
        }

        const renderPostProcessingSettings = templateScope.struct<SceneNodes.RenderPostProcessingSettings>("RenderPostProcessingSettings", {
            type: "RenderPostProcessingSettings",
            templateDepth,
            id: ThisStructID,
            path: newPath,
            mode,
            exposure: scope.pureLambda(evaluator.evaluate(ev), (ev) => Math.pow(2, ev ?? 0), "exposure"),
            whiteBalance: scope.pureLambda(evaluator.evaluate(whiteBalance), (whiteBalance) => whiteBalance ?? undefined, "whiteBalance"),
            toneMapping,
            lutUrl,
            transparent,
            composite,
            backgroundColor: addDataObjectReference(backgroundImage) ?? backgroundColor,
            mask: addDataObjectReference(mask),
            processShadows,
            shadowInner,
            shadowOuter,
            shadowFalloff,
            shadowOpacity,
            autoCrop,
            autoCropMargin,
        })

        displayList.push(renderPostProcessingSettings)

        return visitNone(this.parameters)
    }
}
